<template>
  <div id="layout-wrapper">
    <NavigationBar/>

    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page header -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Extended Absence Report</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="index.html">Home</a></li>
                    <li class="breadcrumb-item">HR Management</li>
                    <li class="breadcrumb-item active">Extended Absence Report</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Filters -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex flex-wrap align-items-center mb-3">
                    <h5 class="card-title me-3 mb-3 mb-md-0">Report Filters</h5>
                    <div class="ms-auto">
                      <button @click="refreshData" class="btn btn-primary me-2">
                        <i class="mdi mdi-refresh me-1"></i> Refresh
                      </button>
                      <button @click="exportReport" class="btn btn-success">
                        <i class="mdi mdi-file-export me-1"></i> Export
                      </button>
                    </div>
                  </div>

                  <div class="row g-3">
                    <div class="col-md-3">
                      <label class="form-label">Department</label>
                      <select v-model="filters.departmentId" class="form-select">
                        <option value="">All Departments</option>
                        <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                          {{ dept.departmentName }}
                        </option>
                      </select>
                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Month</label>
                      <input v-model="filters.selectedMonth" type="month" class="form-control">
                    </div>

                    <div class="col-md-2">
                      <label class="form-label">Threshold (Days)</label>
                      <input v-model.number="filters.absenceThreshold" type="number" min="1" max="60" class="form-control">
                    </div>

                    <div class="col-md-4">
                      <label class="form-label">Search</label>
                      <input
                          type="text"
                          v-model="filters.search"
                          class="form-control"
                          placeholder="Search by name or ID..."
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Statistics cards -->
          <div class="row">
            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-danger rounded-circle text-white">
                          <i class="mdi mdi-account-alert font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="font-size-15 mb-1">Extended Absences</h6>
                      <h4 class="mb-0">{{ summary.totalExtendedAbsences || 0 }}</h4>
                      <span class="text-muted font-size-12">
                        {{ summary.absencePercentage || 0 }}% of workforce
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-danger rounded-circle text-white">
                          <i class="mdi mdi-alert-circle font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="font-size-15 mb-1">High Risk (30+ days)</h6>
                      <h4 class="mb-0">{{ summary.highRiskCount || 0 }}</h4>
                      <span class="text-danger font-size-12">Immediate attention</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-warning rounded-circle text-white">
                          <i class="mdi mdi-alert font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="font-size-15 mb-1">Medium Risk (20-29 days)</h6>
                      <h4 class="mb-0">{{ summary.mediumRiskCount || 0 }}</h4>
                      <span class="text-warning font-size-12">Requires follow-up</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6 mb-4">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="avatar-sm">
                        <span class="avatar-title bg-info rounded-circle text-white">
                          <i class="mdi mdi-information font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="font-size-15 mb-1">Low Risk (15-19 days)</h6>
                      <h4 class="mb-0">{{ summary.lowRiskCount || 0 }}</h4>
                      <span class="text-info font-size-12">Monitor situation</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Department breakdown -->
          <div class="row">
            <div class="col-xl-6 col-lg-6 mb-4">
              <div class="card">
                <div class="card-header">
                  <h5 class="card-title mb-0">Department Breakdown</h5>
                </div>
                <div class="card-body">
                  <div v-if="summary.departmentAbsences && summary.departmentAbsences.length > 0">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead>
                        <tr>
                          <th>Department</th>
                          <th class="text-center">Count</th>
                          <th class="text-center">Percentage</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="dept in summary.departmentAbsences" :key="dept.departmentName">
                          <td>{{ dept.departmentName }}</td>
                          <td class="text-center">{{ dept.count }}</td>
                          <td class="text-center">
                            {{ ((dept.count / summary.totalExtendedAbsences) * 100).toFixed(1) }}%
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div v-else class="text-center p-4">
                    <p class="text-muted">No department data available</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-6 col-lg-6 mb-4">
              <div class="card">
                <div class="card-header">
                  <h5 class="card-title mb-0">Risk Level Summary</h5>
                </div>
                <div class="card-body">
                  <div v-if="summary.totalExtendedAbsences > 0">
                    <div class="mb-3">
                      <div class="d-flex justify-content-between">
                        <span>High Risk (30+ days)</span>
                        <span class="text-danger fw-bold">{{ summary.highRiskCount }}</span>
                      </div>
                      <div class="progress mt-1" style="height: 6px;">
                        <div class="progress-bar bg-danger"
                             :style="`width: ${(summary.highRiskCount / summary.totalExtendedAbsences) * 100}%`"></div>
                      </div>
                    </div>

                    <div class="mb-3">
                      <div class="d-flex justify-content-between">
                        <span>Medium Risk (20-29 days)</span>
                        <span class="text-warning fw-bold">{{ summary.mediumRiskCount }}</span>
                      </div>
                      <div class="progress mt-1" style="height: 6px;">
                        <div class="progress-bar bg-warning"
                             :style="`width: ${(summary.mediumRiskCount / summary.totalExtendedAbsences) * 100}%`"></div>
                      </div>
                    </div>

                    <div class="mb-0">
                      <div class="d-flex justify-content-between">
                        <span>Low Risk (15-19 days)</span>
                        <span class="text-info fw-bold">{{ summary.lowRiskCount }}</span>
                      </div>
                      <div class="progress mt-1" style="height: 6px;">
                        <div class="progress-bar bg-info"
                             :style="`width: ${(summary.lowRiskCount / summary.totalExtendedAbsences) * 100}%`"></div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center p-4">
                    <p class="text-muted">No risk data available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Employee list -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header">
                  <h5 class="card-title">Employees with Extended Absence ({{ filters.absenceThreshold }}+ days)</h5>
                </div>
                <div class="card-body">

                  <!-- Loading -->
                  <div v-if="loading" class="text-center p-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-2">Loading absence data...</p>
                  </div>

                  <!-- Empty state -->
                  <div v-else-if="!extendedAbsenceEmployees.length" class="text-center p-5">
                    <i class="mdi mdi-account-search font-size-48 text-muted"></i>
                    <h5 class="mt-2">No employees with extended absence</h5>
                    <p class="text-muted">Try adjusting your filters or threshold settings</p>
                  </div>

                  <!-- Employee table -->
                  <div v-else class="table-responsive">
                    <table class="table table-hover align-middle">
                      <thead class="table-light">
                      <tr>
                        <th>Employee</th>
                        <th>Department</th>
                        <th>Contact</th>
                        <th class="text-center">Absent Days</th>
                        <th>Risk Level</th>
                        <th>Last Seen</th>
                        <th class="text-center">Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="employee in extendedAbsenceEmployees" :key="employee.employeeId">
                        <td>
                          <div>
                            <h6 class="mb-0">{{ employee.employeeName }}</h6>
                            <small class="text-muted">ID: {{ employee.employeeId }}</small>
                            <small v-if="employee.designation" class="d-block text-muted">{{ employee.designation }}</small>
                          </div>
                        </td>
                        <td>{{ employee.departmentName }}</td>
                        <td>
                          <div v-if="employee.email">
                            <i class="mdi mdi-email-outline me-1"></i>{{ employee.email }}
                          </div>
                          <div v-if="employee.phone">
                            <i class="mdi mdi-phone-outline me-1"></i>{{ employee.phone }}
                          </div>
                          <div v-if="!employee.email && !employee.phone" class="text-muted">No contact info</div>
                        </td>
                        <td class="text-center">
                          <span class="badge" :class="getAbsenceBadgeClass(employee.consecutiveAbsentDays)">
                            {{ employee.consecutiveAbsentDays }} days
                          </span>
                        </td>
                        <td>
                          <span class="badge" :class="getRiskBadgeClass(employee.riskLevel)">
                            {{ employee.riskLevel }}
                          </span>
                        </td>
                        <td>
                          <div v-if="employee.lastAttendance">
                            {{ formatDate(employee.lastAttendance) }}
                            <small class="d-block text-muted">{{ getDaysAgo(employee.lastAttendance) }}</small>
                          </div>
                          <div v-else class="text-danger">No record</div>
                        </td>
                        <td class="text-center">
                          <button @click="viewDetails(employee)" class="btn btn-sm btn-info me-1" title="View Details">
                            <i class="mdi mdi-eye"></i>
                          </button>
                          <button @click="contactEmployee(employee)" class="btn btn-sm btn-primary" title="Contact Employee"
                                  :disabled="!employee.email && !employee.phone">
                            <i class="mdi mdi-email-send"></i>
                          </button>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Pagination -->
                  <div v-if="summary.totalPages > 1" class="d-flex justify-content-center mt-4">
                    <nav>
                      <ul class="pagination">
                        <li class="page-item" :class="{ disabled: filters.page <= 1 }">
                          <a class="page-link" href="#" @click.prevent="changePage(filters.page - 1)">Previous</a>
                        </li>
                        <li v-for="page in getPageNumbers()" :key="page" class="page-item"
                            :class="{ active: page === filters.page }">
                          <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                        </li>
                        <li class="page-item" :class="{ disabled: filters.page >= summary.totalPages }">
                          <a class="page-link" href="#" @click.prevent="changePage(filters.page + 1)">Next</a>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Employee Details Modal -->
          <div class="modal fade" id="detailsModal" tabindex="-1">
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">Employee Absence Details</h5>
                  <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                </div>
                <div class="modal-body">
                  <div v-if="selectedEmployee">
                    <div class="row mb-4">
                      <div class="col-md-6">
                        <h5>{{ selectedEmployee.employeeName }}</h5>
                        <p class="text-muted mb-1">{{ selectedEmployee.designation || 'No Designation' }}</p>
                        <p class="text-muted mb-1">ID: {{ selectedEmployee.employeeId }}</p>
                        <p class="text-muted">Dept: {{ selectedEmployee.departmentName }}</p>
                      </div>
                      <div class="col-md-6 text-md-end">
                        <span class="badge bg-danger fs-6 mb-2">{{ selectedEmployee.consecutiveAbsentDays }} Days Absent</span>
                        <p v-if="selectedEmployee.email" class="mb-1">
                          <i class="mdi mdi-email-outline me-1"></i>{{ selectedEmployee.email }}
                        </p>
                        <p v-if="selectedEmployee.phone" class="mb-0">
                          <i class="mdi mdi-phone-outline me-1"></i>{{ selectedEmployee.phone }}
                        </p>
                      </div>
                    </div>

                    <div class="row">
                      <div class="col-md-6">
                        <h6>Absence Information</h6>
                        <ul class="list-unstyled">
                          <li class="mb-2">
                            <strong>Consecutive Absent Days:</strong> {{ selectedEmployee.consecutiveAbsentDays }}
                          </li>
                          <li class="mb-2">
                            <strong>Risk Level:</strong>
                            <span class="badge" :class="getRiskBadgeClass(selectedEmployee.riskLevel)">
                              {{ selectedEmployee.riskLevel }}
                            </span>
                          </li>
                          <li class="mb-2">
                            <strong>Last Attendance:</strong>
                            <span v-if="selectedEmployee.lastAttendance">
                              {{ formatDate(selectedEmployee.lastAttendance) }}
                            </span>
                            <span v-else class="text-danger">No record</span>
                          </li>
                        </ul>
                      </div>
                      <div class="col-md-6">
                        <h6>Recent Leave Records</h6>
                        <div v-if="selectedEmployee.recentLeaves && selectedEmployee.recentLeaves.length">
                          <div v-for="leave in selectedEmployee.recentLeaves" :key="leave.startDate" class="mb-2">
                            <div class="small">
                              <strong>{{ leave.leaveTypeName }}</strong><br>
                              {{ formatDate(leave.startDate) }} to {{ formatDate(leave.endDate) }}
                            </div>
                          </div>
                        </div>
                        <div v-else class="text-muted">No recent approved leaves</div>
                      </div>
                    </div>

                    <div v-if="selectedEmployee.absentDates && selectedEmployee.absentDates.length" class="mt-4">
                      <h6>Recent Absent Dates</h6>
                      <div class="row">
                        <div v-for="absent in selectedEmployee.absentDates.slice(-10)" :key="absent.date" class="col-md-4 mb-1">
                          <small>
                            <i class="mdi mdi-calendar-remove text-danger me-1"></i>
                            {{ formatDate(absent.date) }} ({{ absent.dayOfWeek }})
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                  <button type="button" class="btn btn-primary" @click="contactEmployee(selectedEmployee)">
                    Contact Employee
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Contact Modal -->
          <div class="modal fade" id="contactModal" tabindex="-1">
            <div class="modal-dialog">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">Contact Employee</h5>
                  <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                </div>
                <div class="modal-body">
                  <div v-if="selectedEmployee">
                    <div class="text-center mb-4">
                      <h5>{{ selectedEmployee.employeeName }}</h5>
                      <p class="text-muted">{{ selectedEmployee.departmentName }}</p>
                    </div>

                    <form @submit.prevent="sendNotification">
                      <div class="mb-3">
                        <label class="form-label">Contact Method</label>
                        <div class="btn-group w-100" role="group">
                          <input type="radio" class="btn-check" id="email" value="email" v-model="contactForm.method">
                          <label class="btn btn-outline-primary" for="email">Email</label>
                          <input type="radio" class="btn-check" id="sms" value="sms" v-model="contactForm.method">
                          <label class="btn btn-outline-primary" for="sms">SMS</label>
                          <input type="radio" class="btn-check" id="both" value="both" v-model="contactForm.method">
                          <label class="btn btn-outline-primary" for="both">Both</label>
                        </div>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Template</label>
                        <select v-model="contactForm.template" class="form-select" @change="updateTemplate">
                          <option value="reminder">Attendance Reminder</option>
                          <option value="warning">Formal Warning</option>
                          <option value="welfare">Welfare Check</option>
                          <option value="custom">Custom Message</option>
                        </select>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Subject</label>
                        <input type="text" v-model="contactForm.subject" class="form-control" required>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Message</label>
                        <textarea v-model="contactForm.message" class="form-control" rows="4" required></textarea>
                      </div>

                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="contactForm.sendToManager" id="sendToManager">
                        <label class="form-check-label" for="sendToManager">
                          Send copy to department manager
                        </label>
                      </div>
                    </form>
                  </div>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                  <button type="button" class="btn btn-primary" @click="sendNotification" :disabled="sending">
                    <span v-if="sending" class="spinner-border spinner-border-sm me-2"></span>
                    Send Message
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import _ from 'lodash';
import NavigationBar from "@/components/Navigation.vue";

export default {
  name: "ExtendedAbsenceReport",
  components: { NavigationBar },
  data() {
    return {
      loading: false,
      sending: false,
      extendedAbsenceEmployees: [],
      departments: [],
      summary: {},
      selectedEmployee: null,
      filters: {
        departmentId: "",
        selectedMonth: this.getCurrentMonth(),
        absenceThreshold: 15,
        search: "",
        page: 1,
        rows_per_page: 10
      },
      contactForm: {
        method: "email",
        template: "reminder",
        subject: "",
        message: "",
        sendToManager: false
      },
      messageTemplates: {
        reminder: {
          subject: "Important: Absence Notification",
          message: "Dear {name},\n\nOur records indicate that you have been absent from work for {days} consecutive working days without approved leave. Please contact HR as soon as possible.\n\nBest regards,\nHR Department"
        },
        warning: {
          subject: "Formal Absence Warning",
          message: "Dear {name},\n\nThis is a formal notification regarding your continued absence from work for {days} consecutive working days. Please contact HR immediately.\n\nBest regards,\nHR Department"
        },
        welfare: {
          subject: "Welfare Check",
          message: "Dear {name},\n\nWe haven't seen you at work for {days} days and we're concerned about your wellbeing. Please let us know if you need any support.\n\nBest regards,\nHR Department"
        },
        custom: {
          subject: "",
          message: ""
        }
      }
    };
  },
  mounted() {
    this.getDepartments();
    this.getExtendedAbsenceData();
  },
  methods: {
    getCurrentMonth() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            }
          })
          .catch((error) => {
            console.error("Error fetching departments:", error);
          });
    },

    getExtendedAbsenceData() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getExtendedAbsenceReport");
      data.append("selectedMonth", this.filters.selectedMonth);
      data.append("absenceThreshold", this.filters.absenceThreshold);
      data.append("page", this.filters.page);
      data.append("rows_per_page", this.filters.rows_per_page);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }
      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.extendedAbsenceEmployees = res.data.data.extendedAbsenceEmployees || [];
              this.summary = res.data.data.summary || {};
            } else {
              console.error("Error:", res.data.message);
              this.extendedAbsenceEmployees = [];
              this.summary = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.extendedAbsenceEmployees = [];
            this.summary = {};
          });
    },

    refreshData() {
      this.getExtendedAbsenceData();
    },

    changePage(page) {
      if (page < 1 || page > this.summary.totalPages || page === this.filters.page) return;
      this.filters.page = page;
      this.getExtendedAbsenceData();
    },

    getPageNumbers() {
      const totalPages = this.summary.totalPages || 0;
      const currentPage = this.filters.page;
      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }
      // Simple pagination logic
      const pages = [];
      if (currentPage > 2) pages.push(1, '...');
      for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 1) pages.push('...', totalPages);
      return pages;
    },

    viewDetails(employee) {
      this.selectedEmployee = employee;
      new bootstrap.Modal(document.getElementById('detailsModal')).show();
    },

    contactEmployee(employee) {
      this.selectedEmployee = employee;
      this.updateTemplate();
      new bootstrap.Modal(document.getElementById('contactModal')).show();
    },

    updateTemplate() {
      if (!this.selectedEmployee) return;

      const template = this.messageTemplates[this.contactForm.template];
      if (!template) return;

      this.contactForm.subject = template.subject;
      let message = template.message;
      message = message.replace(/{name}/g, this.selectedEmployee.employeeName);
      message = message.replace(/{days}/g, this.selectedEmployee.consecutiveAbsentDays);
      this.contactForm.message = message;
    },

    sendNotification() {
      if (!this.selectedEmployee || !this.contactForm.subject || !this.contactForm.message) return;

      this.sending = true;

      const data = new FormData();
      data.append("function", "sendEmployeeNotification");
      data.append("employeeId", this.selectedEmployee.employeeId);
      data.append("subject", this.contactForm.subject);
      data.append("message", this.contactForm.message);
      data.append("contactMethod", this.contactForm.method);
      data.append("sendToManager", this.contactForm.sendToManager ? 1 : 0);

      execute(data, authUrl)
          .then((res) => {
            this.sending = false;
            if (res.data.success) {
              this.$toast.success("Message sent successfully");
              const modal = bootstrap.Modal.getInstance(document.getElementById('contactModal'));
              if (modal) modal.hide();
            } else {
              this.$toast.error("Failed to send message: " + res.data.message);
            }
          })
          .catch((error) => {
            this.sending = false;
            console.error("Error sending notification:", error);
            this.$toast.error("Network error when sending message");
          });
    },

    exportReport() {
      let csvContent = "data:text/csv;charset=utf-8,";
      csvContent += "Employee ID,Employee Name,Department,Email,Phone,Consecutive Absent Days,Risk Level,Last Attendance\n";

      this.extendedAbsenceEmployees.forEach(employee => {
        csvContent += [
          employee.employeeId,
          `"${employee.employeeName}"`,
          `"${employee.departmentName}"`,
          employee.email || 'N/A',
          employee.phone || 'N/A',
          employee.consecutiveAbsentDays,
          employee.riskLevel,
          employee.lastAttendance ? this.formatDate(employee.lastAttendance) : 'No record'
        ].join(',') + "\n";
      });

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `extended-absence-report-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    },

    getDaysAgo(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const today = new Date();
      const diffTime = Math.abs(today - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    },

    getAbsenceBadgeClass(days) {
      if (days >= 30) return 'bg-danger';
      if (days >= 20) return 'bg-warning';
      return 'bg-info';
    },

    getRiskBadgeClass(riskLevel) {
      switch(riskLevel) {
        case 'High': return 'bg-danger';
        case 'Medium': return 'bg-warning';
        case 'Low': return 'bg-info';
        default: return 'bg-secondary';
      }
    }
  },
  watch: {
    'filters.departmentId': _.debounce(function() {
      this.filters.page = 1;
      this.getExtendedAbsenceData();
    }, 300),

    'filters.selectedMonth': _.debounce(function() {
      this.filters.page = 1;
      this.getExtendedAbsenceData();
    }, 300),

    'filters.absenceThreshold': _.debounce(function() {
      this.filters.page = 1;
      this.getExtendedAbsenceData();
    }, 300),

    'filters.search': _.debounce(function() {
      this.filters.page = 1;
      this.getExtendedAbsenceData();
    }, 500)
  }
};
</script>

<style scoped>
.avatar-sm {
  height: 3rem;
  width: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  border-radius: 50%;
}

@media (max-width: 768px) {
  .table-responsive {
    border: 0;
  }
}
</style>